import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useCreateClaim, type ClaimFrequency, type DxPointers } from '@/api/claims';
import { useClientNames, useClient } from '@/api/clients';
import { useUserDirectory } from '@/api/users';
import { useServiceCodes } from '@/api/serviceCodes';
import { usePayers } from '@/api/payers';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { FormField, Input, Textarea } from '@/components/ui/FormField';
import { Alert } from '@/components/ui/Alert';
import { SelectField } from '../clients/SelectField';

/*
 * Manual claim entry. Sessions auto-create claims on finalize — this page is
 * for the exception cases (missed-finalize backfills, off-cycle corrections).
 * It pre-fills payer / auth / diagnoses from the selected client record so
 * the biller doesn't re-type PHI we already have, but every field is editable.
 *
 * PR / paid / allowed are intentionally NOT in this form — new claims start
 * at Ready and get their adjudication amounts later via Record Payment.
 */

const POS_OPTIONS: Array<{ value: string; label: string }> = [
    { value: '02', label: 'Telehealth (02)' },
    { value: '10', label: 'Telehealth — Patient Home (10)' },
    { value: '11', label: 'Office (11)' },
    { value: '12', label: 'Home (12)' },
    { value: '99', label: 'Other (99)' },
];

export function BillingNewPage() {
    const navigate = useNavigate();
    const canEdit = useAuthStore((s) => s.hasPermission('edit_billing'));

    const { data: clientsList, isLoading: clientsLoading } = useClientNames();
    const { data: staffList }       = useUserDirectory();
    const { data: serviceCodes }    = useServiceCodes();
    const { data: payers }          = usePayers();

    const [clientId, setClientId]       = useState('');
    const [staffId, setStaffId]         = useState('');
    const [serviceDate, setServiceDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [serviceCode, setServiceCode] = useState('');
    const [mod1, setMod1]               = useState('');
    const [mod2, setMod2]               = useState('');
    const [mod3, setMod3]               = useState('');
    const [mod4, setMod4]               = useState('');
    const [pos, setPos]                 = useState('11');
    const [units, setUnits]             = useState('');
    const [unitRate, setUnitRate]       = useState('');
    const [amount, setAmount]           = useState('');
    const [payer, setPayer]             = useState('');
    const [payerId, setPayerId]         = useState('');
    const [authNumber, setAuthNumber]   = useState('');
    const [referralNumber, setReferralNumber] = useState('');
    const [frequency, setFrequency]     = useState<ClaimFrequency>('original');
    const [originalClaimNumber, setOriginalClaimNumber] = useState('');
    const [dx1, setDx1]                 = useState('');
    const [dx2, setDx2]                 = useState('');
    const [dxPointers, setDxPointers]   = useState<DxPointers>('A');
    const [serviceStartTime, setServiceStartTime] = useState('');
    const [serviceEndTime, setServiceEndTime]     = useState('');
    const [notes, setNotes]             = useState('');

    const { data: client } = useClient(clientId || undefined);
    const createClaim = useCreateClaim();

    // Auto-fill from the selected client. We only overwrite fields the user
    // hasn't touched (empty string check), so hand-entered values survive a
    // client change.
    useEffect(() => {
        if (!client) return;
        const ins1 = client.ins1 ?? {};
        setPayer((p) => p || ins1.payer || '');
        setPayerId((p) => p || ins1.id || '');
        setDx1((d) => d || client.dx1Code || client.dx1 || '');
        setDx2((d) => d || client.dx2Code || client.dx2 || '');
        const activeAuth = (client.auths || []).find((a) => {
            const end = a.end ? new Date(a.end) : null;
            if (end && end < new Date(serviceDate)) return false;
            return true;
        });
        if (activeAuth) setAuthNumber((v) => v || activeAuth.number || '');
    }, [client, serviceDate]);

    // Auto-calc billed amount when both units and rate change (only while
    // the user hasn't overridden it).
    const [amountTouched, setAmountTouched] = useState(false);
    useEffect(() => {
        if (amountTouched) return;
        const u = Number(units) || 0;
        const r = Number(unitRate) || 0;
        if (u > 0 && r > 0) setAmount((u * r).toFixed(2));
    }, [units, unitRate, amountTouched]);

    const activeCodes = useMemo(
        () => (serviceCodes ?? []).filter((c) => c.status === 'Active'),
        [serviceCodes],
    );
    const activePayers = useMemo(
        () => (payers ?? []).filter((p) => p.status === 'Active'),
        [payers],
    );

    const clientName =
        client ? `${client.first} ${client.last}`.trim()
        : (clientsList?.find((c) => c.id === clientId)
            ? `${clientsList.find((c) => c.id === clientId)!.first} ${clientsList.find((c) => c.id === clientId)!.last}`.trim()
            : '');

    const staff = staffList?.find((s) => s.id === staffId);
    const staffName = staff ? `${staff.first} ${staff.last}`.trim() : '';

    async function onSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (!clientId || !staffId || !serviceCode || !serviceDate) return;
        try {
            const base = {
                clientId,
                client: clientName,
                staff: staffName,
                sessionId: null,
                serviceCode,
                mod1: mod1.trim(),
                mod2: mod2.trim(),
                mod3: mod3.trim(),
                mod4: mod4.trim(),
                pos,
                serviceDate,
                units: Number(units) || 0,
                unitRate: Number(unitRate) || 0,
                amount: Number(amount) || 0,
                payer: payer.trim(),
                notes: notes.trim(),
                status: 'Ready' as const,
            };
            const created = await createClaim.mutateAsync({
                ...base,
                payerId: payerId.trim(),
                authNumber: authNumber.trim(),
                referralNumber: referralNumber.trim(),
                frequency,
                originalClaimNumber: originalClaimNumber.trim(),
                dx1: dx1.trim(),
                dx2: dx2.trim(),
                dxPointers,
                serviceStartTime: serviceStartTime.trim(),
                serviceEndTime: serviceEndTime.trim(),
            });
            navigate(`/billing/${created.id}`);
        } catch {
            /* surfaced below via createClaim.error */
        }
    }

    const serverError = (() => {
        const e = createClaim.error;
        if (!e) return null;
        if (e instanceof AxiosError) return e.response?.data?.error ?? 'Failed to create claim';
        return 'Unexpected error';
    })();

    if (!canEdit) {
        return (
            <div className="p-6 max-w-[900px] mx-auto">
                <Link to="/billing" className="text-xs text-text-muted hover:text-text inline-flex items-center gap-1">
                    <ArrowLeft className="w-3 h-3" /> Back to claims
                </Link>
                <div className="mt-4 p-6 text-center text-text-subtle">You don't have permission to create claims.</div>
            </div>
        );
    }

    return (
        <div className="p-6 max-w-[900px] mx-auto">
            <Link to="/billing" className="text-xs text-text-muted hover:text-text inline-flex items-center gap-1">
                <ArrowLeft className="w-3 h-3" /> Back to claims
            </Link>

            <header className="mt-3 mb-5">
                <h1 className="font-display text-2xl font-bold text-text">New Claim</h1>
                <p className="text-xs text-text-subtle">
                    Most claims are created automatically when a session is finalized. Use this form
                    only for corrections or off-cycle billing.
                </p>
            </header>

            <Card className="p-5">
                <form onSubmit={onSubmit} noValidate>
                    {serverError && <Alert variant="danger" className="mb-4">{serverError}</Alert>}

                    <div className="grid grid-cols-2 gap-x-4">
                        <FormField label="Client" required>
                            {(id) => (
                                <SelectField
                                    id={id}
                                    value={clientId}
                                    onChange={(e) => setClientId(e.target.value)}
                                    disabled={clientsLoading}
                                >
                                    <option value="">{clientsLoading ? 'Loading…' : 'Select a client…'}</option>
                                    {(clientsList ?? [])
                                        .slice()
                                        .sort((a, b) => `${a.first} ${a.last}`.localeCompare(`${b.first} ${b.last}`))
                                        .map((c) => (
                                            <option key={c.id} value={c.id}>
                                                {c.first} {c.last}
                                            </option>
                                        ))}
                                </SelectField>
                            )}
                        </FormField>

                        <FormField label="Rendering Staff" required>
                            {(id) => (
                                <SelectField id={id} value={staffId} onChange={(e) => setStaffId(e.target.value)}>
                                    <option value="">Select staff…</option>
                                    {(staffList ?? []).map((s) => (
                                        <option key={s.id} value={s.id}>
                                            {s.first} {s.last} ({s.roleType})
                                        </option>
                                    ))}
                                </SelectField>
                            )}
                        </FormField>

                        <FormField label="Service Date" required>
                            {(id) => (
                                <Input
                                    id={id}
                                    type="date"
                                    min="1900-01-01"
                                    max={new Date().toISOString().slice(0, 10)}
                                    value={serviceDate}
                                    onChange={(e) => setServiceDate(e.target.value)}
                                />
                            )}
                        </FormField>

                        <FormField label="Place of Service" required>
                            {(id) => (
                                <SelectField id={id} value={pos} onChange={(e) => setPos(e.target.value)}>
                                    {POS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </SelectField>
                            )}
                        </FormField>

                        <FormField label="CPT / Service Code" required>
                            {(id) => (
                                <SelectField id={id} value={serviceCode} onChange={(e) => setServiceCode(e.target.value)}>
                                    <option value="">Select…</option>
                                    {activeCodes.map((c) => (
                                        <option key={c.id} value={c.code}>{c.code} — {c.desc}</option>
                                    ))}
                                </SelectField>
                            )}
                        </FormField>

                        <div className="grid grid-cols-4 gap-x-2">
                            <FormField label="Mod 1">
                                {(id) => <Input id={id} value={mod1} maxLength={2} onChange={(e) => setMod1(e.target.value.toUpperCase())} />}
                            </FormField>
                            <FormField label="Mod 2">
                                {(id) => <Input id={id} value={mod2} maxLength={2} onChange={(e) => setMod2(e.target.value.toUpperCase())} />}
                            </FormField>
                            <FormField label="Mod 3">
                                {(id) => <Input id={id} value={mod3} maxLength={2} onChange={(e) => setMod3(e.target.value.toUpperCase())} />}
                            </FormField>
                            <FormField label="Mod 4">
                                {(id) => <Input id={id} value={mod4} maxLength={2} onChange={(e) => setMod4(e.target.value.toUpperCase())} />}
                            </FormField>
                        </div>

                        <FormField label="Units" required>
                            {(id) => (
                                <Input
                                    id={id}
                                    type="number"
                                    min={0}
                                    step="1"
                                    value={units}
                                    onChange={(e) => setUnits(e.target.value)}
                                />
                            )}
                        </FormField>
                        <FormField label="Unit Rate" required>
                            {(id) => (
                                <Input
                                    id={id}
                                    type="number"
                                    min={0}
                                    step="0.01"
                                    value={unitRate}
                                    onChange={(e) => setUnitRate(e.target.value)}
                                />
                            )}
                        </FormField>

                        <FormField label="Billed Amount" required hint="Auto-calculated from units × rate. Override if needed." className="col-span-2">
                            {(id) => (
                                <Input
                                    id={id}
                                    type="number"
                                    min={0}
                                    step="0.01"
                                    value={amount}
                                    onChange={(e) => { setAmount(e.target.value); setAmountTouched(true); }}
                                />
                            )}
                        </FormField>

                        <FormField label="Service Start Time" hint="24-hour HH:MM; used on 837P.">
                            {(id) => (
                                <Input
                                    id={id}
                                    type="time"
                                    value={serviceStartTime}
                                    onChange={(e) => setServiceStartTime(e.target.value)}
                                />
                            )}
                        </FormField>
                        <FormField label="Service End Time" hint="24-hour HH:MM; used on 837P.">
                            {(id) => (
                                <Input
                                    id={id}
                                    type="time"
                                    value={serviceEndTime}
                                    onChange={(e) => setServiceEndTime(e.target.value)}
                                />
                            )}
                        </FormField>

                        <FormField label="Payer">
                            {(id) => (
                                <SelectField
                                    id={id}
                                    value={payer}
                                    onChange={(e) => {
                                        const name = e.target.value;
                                        setPayer(name);
                                        const match = activePayers.find((p) => p.name === name);
                                        if (match) setPayerId(match.payerId);
                                    }}
                                >
                                    <option value="">Select a payer…</option>
                                    {activePayers.map((p) => (
                                        <option key={p.id} value={p.name}>{p.name}</option>
                                    ))}
                                    {payer && !activePayers.find((p) => p.name === payer) && (
                                        <option value={payer}>{payer} (from client)</option>
                                    )}
                                </SelectField>
                            )}
                        </FormField>
                        <FormField label="Payer ID (EDI)">
                            {(id) => <Input id={id} value={payerId} onChange={(e) => setPayerId(e.target.value)} />}
                        </FormField>

                        <FormField label="Authorization # (REF*G1)">
                            {(id) => <Input id={id} value={authNumber} onChange={(e) => setAuthNumber(e.target.value)} />}
                        </FormField>
                        <FormField label="Referral # (REF*9F)">
                            {(id) => <Input id={id} value={referralNumber} onChange={(e) => setReferralNumber(e.target.value)} />}
                        </FormField>

                        <FormField
                            label="Claim frequency"
                            hint="Original, corrected replacement, or void of a prior claim."
                        >
                            {(id) => (
                                <SelectField
                                    id={id}
                                    value={frequency}
                                    onChange={(e) => setFrequency(e.target.value as ClaimFrequency)}
                                >
                                    <option value="original">Original (1)</option>
                                    <option value="corrected">Corrected (7)</option>
                                    <option value="void">Void (8)</option>
                                </SelectField>
                            )}
                        </FormField>
                        <FormField
                            label="Original claim # (REF*F8)"
                            hint="Required when frequency is Corrected or Void."
                        >
                            {(id) => (
                                <Input
                                    id={id}
                                    value={originalClaimNumber}
                                    onChange={(e) => setOriginalClaimNumber(e.target.value)}
                                    disabled={frequency === 'original'}
                                />
                            )}
                        </FormField>

                        <FormField label="Diagnosis — Primary (ICD-10)">
                            {(id) => <Input id={id} value={dx1} onChange={(e) => setDx1(e.target.value.toUpperCase())} />}
                        </FormField>
                        <FormField label="Diagnosis — Secondary (ICD-10)">
                            {(id) => <Input id={id} value={dx2} onChange={(e) => setDx2(e.target.value.toUpperCase())} />}
                        </FormField>

                        <FormField
                            label="Diagnosis pointer"
                            hint="Which diagnosis applies to this service line (CMS-1500 Box 24E)."
                            className="col-span-2"
                        >
                            {(id) => (
                                <SelectField
                                    id={id}
                                    value={dxPointers}
                                    onChange={(e) => setDxPointers(e.target.value as DxPointers)}
                                >
                                    <option value="A">A — Primary only</option>
                                    <option value="B" disabled={!dx2.trim()}>B — Secondary only</option>
                                    <option value="AB" disabled={!dx2.trim()}>A + B — Both</option>
                                </SelectField>
                            )}
                        </FormField>

                        <FormField label="Internal Notes" className="col-span-2">
                            {(id) => (
                                <Textarea
                                    id={id}
                                    rows={3}
                                    placeholder="Not sent on the claim."
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                />
                            )}
                        </FormField>
                    </div>

                    <div className="flex items-center justify-end gap-2 mt-6 pt-4 border-t border-border">
                        <Button type="button" variant="ghost" onClick={() => navigate('/billing')} disabled={createClaim.isPending}>
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="primary"
                            disabled={createClaim.isPending || !clientId || !staffId || !serviceCode}
                        >
                            {createClaim.isPending ? (
                                <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Creating…</>
                            ) : 'Create claim'}
                        </Button>
                    </div>
                </form>
            </Card>
        </div>
    );
}
